'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import CountUp from '@/components/CountUp';

function useScrollReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

const gearItems = [
  { category: 'DAW', items: ['Logic Pro X', 'Reaper'] },
  { category: 'Drums', items: ['SJC Shells', 'Meinl Cymbals', 'Evans Drum Heads', 'DW Hardware'] },
  { category: 'Plugins', items: ['300+', 'Slate Digital', 'Waves', 'Neural DSP', 'JST Tones'] },
  { category: 'Monitoring', items: ["Yamaha NS-10M\'s", 'Beyerdynamic Headphones'] },
  { category: 'Hardware', items: ['Shure Microphones', 'Behringer Pre Amps'] },
];

const discography = [
  {
    year: '2025',
    title: '(Not in your) Nature',
    artist: 'Covert UK',
    role: 'Recording · Mixing · Songwriting · Session Drumming',
  },
  {
    year: '2025',
    title: 'Twisted Mind',
    artist: 'Covert UK',
    role: 'Recording · Mixing · Songwriting · Session Drumming',
  },
  {
    year: '2025',
    title: 'Nothing More',
    artist: 'Dystopian Sun',
    role: 'Drum Play-through · Session Drumming',
  },
  {
    year: '2024',
    title: 'Ribs (on show)',
    artist: 'Covert UK',
    role: 'Recording · Mixing · Songwriting · Session Drumming',
  },
];

const stats = [{ value: '5+', label: 'Years in Metal' }];

export default function About() {
  useScrollReveal();

  return (
    <div
      style={{ minHeight: '100vh', background: '#000000', color: '#ffffff', overflowX: 'hidden' }}
    >
      <Header />

      {/* ─── PAGE HEADER ─── */}
      <section
        style={{
          paddingTop: '60px',
          paddingBottom: '60px',
          borderBottom: '1px solid #ff1a1a',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '380px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={{ position: 'absolute', inset: 0 }}>
          <AppImage
            src="/assets/images/diggity-5.jpg"
            alt="Black and white live drumming performance photo of Diggity Glover"
            fill
            className="object-cover object-center"
            priority
          />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.8)' }} />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(ellipse at top left, rgba(255,26,26,0.12) 0%, transparent 60%)',
              pointerEvents: 'none',
            }}
          />
        </div>
        <div
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
            padding: '0 24px',
            position: 'relative',
            zIndex: 10,
          }}
        >
          <p
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#ff1a1a',
              marginBottom: '16px',
            }}
          >
            // About
          </p>
          <h1
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 900,
              textTransform: 'uppercase',
              fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
              lineHeight: '0.95',
              color: '#ffffff',
              textShadow: '0 0 20px rgba(255,26,26,0.7)',
              marginBottom: '16px',
            }}
          >
            Diggity Glover
          </h1>
          <p
            style={{
              fontFamily: 'Manrope, sans-serif',
              color: '#cccccc',
              fontSize: '1.1rem',
              maxWidth: '500px',
              lineHeight: '1.6',
            }}
          >
            Metal Mixing Engineer · Session Drummer · Producer · Recording Engineer
          </p>
        </div>
      </section>

      {/* ─── STATS BAR ─── */}
      <div
        style={{ background: '#111111', borderBottom: '1px solid #ff1a1a', padding: '20px 24px' }}
      >
        <div
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
            display: 'flex',
            gap: '48px',
            flexWrap: 'wrap',
          }}
        >
          {stats.map((s) => (
            <div key={s.label}>
              <span
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 900,
                  fontSize: '2rem',
                  color: '#ff1a1a',
                  textShadow: '0 0 10px rgba(255,26,26,0.6)',
                  display: 'block',
                }}
              >
                <CountUp value={s.value} />
              </span>
              <span
                style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.7rem',
                  color: '#cccccc',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ─── BIO ─── */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '64px',
              alignItems: 'center',
            }}
          >
            <div className="reveal" style={{ position: 'relative' }}>
              <div style={{ border: '1px solid #ff1a1a', overflow: 'hidden' }}>
                <AppImage
                  src="/assets/images/diggity-15.jpg"
                  alt="Diggity Glover black and white portrait, performing live"
                  width={700}
                  height={800}
                  className="w-full object-cover"
                  style={{ filter: 'grayscale(20%) contrast(1.1)', display: 'block' }}
                  priority
                />
              </div>
            </div>

            <div className="reveal">
              {/* Panel with red header strip */}
              <div style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}>
                <div
                  style={{
                    background: '#ff1a1a',
                    color: '#000000',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    padding: '8px 16px',
                  }}
                >
                  Origin Story
                </div>
                <div style={{ padding: '28px' }}>
                  <h2
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 900,
                      textTransform: 'uppercase',
                      fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
                      lineHeight: '1.1',
                      color: '#ffffff',
                      textShadow: '0 0 10px rgba(255,26,26,0.5)',
                      marginBottom: '20px',
                    }}
                  >
                    Metal Drummer, Recording, Mixing and Mastering Engineer
                  </h2>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      fontFamily: 'Manrope, sans-serif',
                      color: '#cccccc',
                      lineHeight: '1.8',
                      fontSize: '1.05rem',
                    }}
                  >
                    <p>
                      Diggity Glover is a young metal drummer, mixing engineer, producer and
                      composer based out of Manchester, England.
                    </p>
                    <p>
                      Known for his work with Covert UK and Dystopian Sun, Diggity is an up and
                      coming producer with great potential!
                    </p>
                    <p>
                      Today, the work Diggity produces spans everything from tracking live sessions
                      to remote mixing and mastering for bands across England.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── GEAR ─── */}
      <section
        style={{
          padding: '80px 24px',
          borderTop: '1px solid rgba(255,26,26,0.3)',
          background: '#111111',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '48px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // Toolkit
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              Gear & Philosophy
            </h2>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '12px',
            }}
          >
            {gearItems.map((gear, i) => (
              <div
                key={gear.category}
                className={`reveal stagger-${i + 1}`}
                style={{ background: '#1a1a1a', border: '1px solid #ff1a1a' }}
              >
                <div
                  style={{
                    background: '#ff1a1a',
                    color: '#000000',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    padding: '6px 12px',
                  }}
                >
                  {gear.category}
                </div>
                <ul
                  style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}
                >
                  {gear.items.map((item) => (
                    <li
                      key={item}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontFamily: 'Manrope, sans-serif',
                        color: '#cccccc',
                        fontSize: '0.95rem',
                      }}
                    >
                      <span
                        style={{
                          width: '4px',
                          height: '4px',
                          background: '#ff1a1a',
                          flexShrink: 0,
                        }}
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── DISCOGRAPHY TABLE ─── */}
      <section style={{ padding: '80px 24px', borderTop: '1px solid rgba(255,26,26,0.3)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="reveal" style={{ marginBottom: '48px' }}>
            <p
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#ff1a1a',
                marginBottom: '12px',
              }}
            >
              // Discography
            </p>
            <h2
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                color: '#ffffff',
                textShadow: '0 0 10px rgba(255,26,26,0.5)',
              }}
            >
              Selected Works
            </h2>
          </div>

          <div
            className="reveal"
            style={{ background: '#1a1a1a', border: '1px solid #ff1a1a', overflow: 'hidden' }}
          >
            <div
              style={{
                background: '#ff1a1a',
                color: '#000000',
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 700,
                fontSize: '0.8rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                padding: '8px 16px',
              }}
            >
              Discography
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="myspace-table">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Title</th>
                    <th>Artist</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {discography.map((d, i) => (
                    <tr key={i}>
                      <td
                        style={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.8rem',
                          color: '#ff1a1a',
                        }}
                      >
                        {d.year}
                      </td>
                      <td style={{ fontWeight: 700, color: '#ffffff' }}>{d.title}</td>
                      <td>{d.artist}</td>
                      <td
                        style={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.75rem',
                          color: '#ff1a1a',
                        }}
                      >
                        {d.role}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section
        style={{
          padding: '80px 24px',
          borderTop: '1px solid rgba(255,26,26,0.3)',
          background: '#111111',
          textAlign: 'center',
        }}
      >
        <div className="reveal" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 900,
              textTransform: 'uppercase',
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              lineHeight: '1',
              color: '#ffffff',
              textShadow: '0 0 15px rgba(255,26,26,0.7)',
              marginBottom: '24px',
            }}
          >
            Ready to Work Together?
          </h2>
          <Link
            href="/contact"
            className="btn-primary"
            style={{ fontSize: '1rem', padding: '14px 40px' }}
          >
            Start a Conversation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
